import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { initAnalyticsIfConsented } from "../lib/analytics";

const STORAGE_KEY = "ttl_cookie_choice";  // "accepted" | "rejected"

export default function CookieNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show if no choice on file. Defer to next tick so the page
    // isn't blocked on first paint.
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("[cookies] localStorage unavailable", err);
      }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[cookies] could not persist consent choice", err);
    }
    setVisible(false);
    // Load GA4 immediately if the visitor accepted — no reload needed.
    if (choice === "accepted") initAnalyticsIfConsented();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 bottom-4 z-50 max-w-sm bg-white shadow-lg border animate-in fade-in slide-in-from-bottom-2 duration-300"
      style={{ borderColor: "var(--border)" }}
      role="dialog"
      aria-label="Cookie notice"
      data-testid="cookie-notice"
    >
      <button
        onClick={() => choose("rejected")}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-[#66726d] hover:text-[#151e1a]"
        data-testid="cookie-dismiss"
      >
        <X size={14} />
      </button>
      <div className="px-5 py-4 pr-8">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[#1e4a35] mb-2">
          A note on cookies
        </p>
        <p className="text-sm text-[#404e48] leading-relaxed">
          We use a functional cookie for editor logins. If you accept, we
          also enable privacy-first analytics (Google Analytics 4, IP
          anonymised) to see which posts resonate. See our{" "}
          <Link
            to="/privacy"
            className="text-[#151e1a] underline decoration-dotted underline-offset-2"
            data-testid="cookie-privacy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => choose("accepted")}
            className="font-mono text-[10px] tracking-widest uppercase bg-[#1e4a35] text-white px-4 py-2 hover:bg-[#404e48] transition-colors"
            data-testid="cookie-accept"
          >
            Accept
          </button>
          <button
            onClick={() => choose("rejected")}
            className="font-mono text-[10px] tracking-widest uppercase text-[#404e48] px-4 py-2 hover:text-[#1e4a35] transition-colors"
            data-testid="cookie-reject"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}
